import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface DailyPoint   { label: string; sends: number; failures: number; }
interface FailureReason { reason: string; count: number; }
interface MetricsData {
  sends24h:        number;
  failures24h:     number;
  failureRate:     number;
  daily:           DailyPoint[];
  topFailures:     FailureReason[];
  pendingHandoffs: number;
  handledHandoffs: number;
  avgResponseMins: number | null;
}

const TOOLTIP_STYLE = {
  contentStyle: {
    background: "#0A1628", border: "1px solid rgba(201,168,76,0.2)",
    borderRadius: 8, fontSize: 11, color: "#F2EBD9",
  },
  labelStyle: { color: "rgba(242,235,217,0.45)" },
};

export default function ChatMetricsPanel() {
  const [data, setData]       = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    axios.get(`${API_BASE}/api/dashboard/message-metrics`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="cmp-loading">Loading metrics…</div>;
  if (error)   return <div className="cmp-error">Failed to load: {error}</div>;
  if (!data)   return null;

  return (
    <div className="cmp-root">
      {/* Stat cards */}
      <div className="cmp-stats">
        <div className="cmp-stat">
          <span className="cmp-stat-label">Sent (24h)</span>
          <span className="cmp-stat-value" style={{ color: "#34d399" }}>
            {data.sends24h.toLocaleString()}
          </span>
        </div>
        <div className="cmp-stat">
          <span className="cmp-stat-label">AI (7d)</span>
          <span className="cmp-stat-value" style={{ color: "#C9A84C" }}>
            {(data.topFailures.find(f => f.reason === "AI replies")?.count ?? 0).toLocaleString()}
          </span>
        </div>
        <div className="cmp-stat">
          <span className="cmp-stat-label">Staff (7d)</span>
          <span className="cmp-stat-value" style={{ color: "#60a5fa" }}>
            {(data.topFailures.find(f => f.reason === "Staff replies")?.count ?? 0).toLocaleString()}
          </span>
        </div>
        <div className="cmp-stat">
          <span className="cmp-stat-label">Avg Response</span>
          <span className="cmp-stat-value" style={{ color: "#60a5fa" }}>
            {data.avgResponseMins == null
              ? "—"
              : data.avgResponseMins >= 60
                ? `${Math.round(data.avgResponseMins / 60)}h`
                : `${data.avgResponseMins}m`}
          </span>
        </div>
      </div>

      {/* Handoff stats */}
      <div className="cmp-section-label">Handoffs</div>
      <div className="cmp-stats">
        <div className="cmp-stat">
          <span className="cmp-stat-label">Pending</span>
          <span className="cmp-stat-value" style={{ color: "#f87171" }}>
            {(data.pendingHandoffs ?? 0).toLocaleString()}
          </span>
        </div>
        <div className="cmp-stat">
          <span className="cmp-stat-label">Handled</span>
          <span className="cmp-stat-value" style={{ color: "#34d399" }}>
            {(data.handledHandoffs ?? 0).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Bar chart */}
      <div className="cmp-section-label">Messages Sent — Last 7 Days</div>
      <div className="cmp-chart">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data.daily} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(201,168,76,0.07)" />
            <XAxis
              dataKey="label"
              tick={{ fill: "rgba(242,235,217,0.4)", fontSize: 10 }}
              axisLine={false} tickLine={false}
            />
            <YAxis
              tick={{ fill: "rgba(242,235,217,0.4)", fontSize: 10 }}
              axisLine={false} tickLine={false}
              allowDecimals={false}
            />
            <Tooltip {...TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: 10, color: "rgba(242,235,217,0.5)", paddingTop: 6 }} />
            <Bar dataKey="sends" name="Messages" fill="#34d399" radius={[3,3,0,0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Sender breakdown */}
      <div className="cmp-section-label">Sender Breakdown — 7 Days</div>
      {data.topFailures.length === 0 ? (
        <p className="cmp-empty">No messages in the last 7 days</p>
      ) : (
        <div className="cmp-failures">
          {data.topFailures.map((f, i) => (
            <div key={i} className="cmp-failure-row">
              <span className="cmp-failure-reason">{f.reason}</span>
              <span className="cmp-failure-count">{f.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
